/**
 * ZIP lookup — owns the zip/zipResult/checking state. Cross-references
 * static ZONES + SI_ZONE for instant feedback, and calls /api/nearest in
 * parallel for the closest-store CTA below.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, Truck, ShieldCheck, Search, ArrowRight } from "lucide-react";
import { getNearest } from "../../lib/api";
import { ZONES, SI_ZONE } from "./deliveryData";

function Stat({ icon, value, label }) {
    return (
        <div className="bg-zen-bg p-5 sm:p-6 flex flex-col gap-2">
            {icon}
            <p className="font-serif text-3xl sm:text-4xl text-zen-ink font-light leading-none">
                {value}
            </p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft">
                {label}
            </p>
        </div>
    );
}

export default function DeliveryZipCheck() {
    const [zip, setZip] = useState("");
    const [zipResult, setZipResult] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkZip = async () => {
        const cleaned = zip.replace(/\D/g, "").slice(0, 5);
        if (cleaned.length !== 5) {
            setZipResult({ ok: false, message: "Enter a 5-digit ZIP." });
            return;
        }
        const allQueens = ZONES.flatMap((z) =>
            z.zips.map((z2) => ({ zip: z2, zone: z.title, eta: z.eta }))
        );
        const queensHit = allQueens.find((r) => r.zip === cleaned);
        const siHit = SI_ZONE.zips.includes(cleaned);
        setChecking(true);
        try {
            const nearest = await getNearest({ zip: cleaned });
            if (queensHit) {
                setZipResult({
                    ok: true,
                    zone: queensHit.zone,
                    eta: queensHit.eta,
                    store: "ZenZest Queens & Long Island",
                    storeSlug: "queens-long-island",
                    nearest,
                });
            } else if (siHit) {
                setZipResult({
                    ok: true,
                    zone: SI_ZONE.title,
                    eta: SI_ZONE.eta,
                    store: "ZenZest Staten Island",
                    storeSlug: "staten-island",
                    nearest,
                });
            } else {
                setZipResult({
                    ok: false,
                    message:
                        "We don't deliver to that ZIP yet. Pickup is still on the table — check the closest store below.",
                    nearest,
                });
            }
        } catch {
            if (queensHit) {
                setZipResult({ ok: true, zone: queensHit.zone, eta: queensHit.eta, store: "ZenZest Queens & Long Island", storeSlug: "queens-long-island" });
            } else if (siHit) {
                setZipResult({ ok: true, zone: SI_ZONE.title, eta: SI_ZONE.eta, store: "ZenZest Staten Island", storeSlug: "staten-island" });
            } else {
                setZipResult({ ok: false, message: "We don't deliver to that ZIP yet." });
            }
        } finally {
            setChecking(false);
        }
    };

    return (
        <section
            id="zip-check"
            data-testid="delivery-zip-check"
            className="bg-zen-bg border-b border-zen-line"
        >
            <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                    Delivery zone lookup
                </p>
                <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                    Drop your ZIP. <span className="italic text-zen-olive">We&rsquo;ll be honest.</span>
                </h2>
                <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl mb-8 leading-relaxed">
                    We don&rsquo;t list zones we can&rsquo;t actually serve. Type your
                    ZIP — we&rsquo;ll show your tier, ETA, and which of our two stores
                    will pack the order. On the edge? We&rsquo;ll tell you that too.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={5}
                        placeholder="Try 11040 · 11001 · 11530 · 10303"
                        value={zip}
                        onChange={(e) =>
                            setZip(e.target.value.replace(/\D/g, "").slice(0, 5))
                        }
                        data-testid="delivery-zip-input"
                        className="flex-1 bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink placeholder:text-zen-ink-soft/60 focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15"
                    />
                    <button
                        onClick={checkZip}
                        disabled={checking || zip.length < 5}
                        data-testid="delivery-zip-submit"
                        className="bg-zen-ink text-zen-cream px-7 py-3 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                        <Search size={13} />
                        {checking ? "Checking…" : "Check coverage"}
                    </button>
                </div>
                {zipResult && (
                    <div
                        data-testid="delivery-zip-result"
                        data-ok={zipResult.ok ? "true" : "false"}
                        className={`mt-6 p-5 border max-w-xl ${
                            zipResult.ok
                                ? "bg-zen-olive/10 border-zen-olive/40"
                                : "bg-zen-surface border-zen-line"
                        }`}
                    >
                        {zipResult.ok ? (
                            <>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-2">
                                    {zipResult.zone} · ETA {zipResult.eta}
                                </p>
                                <p className="font-serif text-xl text-zen-ink font-light leading-tight mb-3">
                                    We deliver to {zip}. Packed from {zipResult.store}.
                                </p>
                                <Link
                                    to={`/menu/${zipResult.storeSlug}`}
                                    className="text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 inline-flex items-center gap-2"
                                >
                                    Start the order <ArrowRight size={12} />
                                </Link>
                            </>
                        ) : (
                            <p className="text-sm text-zen-ink leading-relaxed">
                                {zipResult.message}
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-zen-line">
                    <Stat icon={<Clock size={16} className="text-zen-olive" />} value="~60 min" label="Local ETA" />
                    <Stat icon={<MapPin size={16} className="text-zen-olive" />} value="82" label="ZIPs covered" />
                    <Stat icon={<Truck size={16} className="text-zen-olive" />} value="Free" label="Delivery fee" />
                    <Stat icon={<ShieldCheck size={16} className="text-zen-olive" />} value="21+" label="NY-licensed" />
                </div>
                <p className="text-xs text-zen-ink-soft mt-6">
                    Flat $100 minimum across every zone — no surge, no add-on fees.
                </p>
            </div>
        </section>
    );
}
